import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Clock, Gauge, UploadCloud, Zap } from "lucide-react";

const SPEED_PRESETS = [
  { label: "Slow DSL / Mobile 3G", speedMbps: 5 },
  { label: "4G LTE / Standard Broadband", speedMbps: 25 },
  { label: "Fast Home Wi-Fi (50 Mbps)", speedMbps: 50 },
  { label: "High-Speed Cable (100 Mbps)", speedMbps: 100 },
  { label: "Fast 5G / Fiber (300 Mbps)", speedMbps: 300 },
  { label: "Gigabit Fiber (1000 Mbps)", speedMbps: 1000 },
];

function formatSeconds(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return "instant";
  if (sec < 60) return `${Math.ceil(sec)} seconds`;
  if (sec < 3600) {
    const m = Math.floor(sec / 60);
    const s = Math.ceil(sec % 60);
    return s === 0 ? `${m} minutes` : `${m}m ${s}s`;
  }
  const h = Math.floor(sec / 3600);
  const m = Math.ceil((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

export function UploadTimeCalculator() {
  const [sizeValue, setSizeValue] = useState<number>(500);
  const [sizeUnit, setSizeUnit] = useState<"MB" | "GB">("MB");
  const [customMbps, setCustomMbps] = useState<number>(100);

  const totalBytes = sizeUnit === "GB" ? sizeValue * 1024 * 1024 * 1024 : sizeValue * 1024 * 1024;
  const totalBits = totalBytes * 8;

  // Assuming ~10% protocol overhead (TCP/IP framing, TLS, chunking)
  const effectiveBits = totalBits * 1.1;

  return (
    <article className="panel legal" style={{ maxWidth: 780, margin: "0 auto" }}>
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(0, 255, 102, 0.1)", borderRadius: 999, border: "1px solid rgba(0, 255, 102, 0.25)", fontSize: 12, color: "#7cff5c", fontFamily: "ui-monospace, monospace", marginBottom: 12 }}>
          <Gauge size={12} /> Free Interactive Tool
        </div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.15, margin: "0 0 14px", fontWeight: 800 }}>
          File Upload &amp; Download Time Calculator
        </h1>
        <p className="hint" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          Estimate exactly how long it takes to upload and download your file based on file size and internet connection speed.
        </p>
      </header>

      {/* Calculator HUD */}
      <div style={{ padding: 22, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 28 }}>
        <div className="row" style={{ marginTop: 0, gap: 16 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>
              File size:
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="number"
                min={1}
                max={sizeUnit === "GB" ? 50 : 50000}
                value={sizeValue}
                onChange={(e) => setSizeValue(Math.max(1, Number(e.target.value)))}
                className="textinput"
                style={{ marginTop: 0, fontSize: 16, fontWeight: 700 }}
              />
              <select
                value={sizeUnit}
                onChange={(e) => setSizeUnit(e.target.value as "MB" | "GB")}
                className="select"
                style={{ margin: 0, padding: "0 12px", height: 42, fontSize: 14 }}
              >
                <option value="MB">MB</option>
                <option value="GB">GB</option>
              </select>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 6 }}>
              Upload speed (Mbps):
            </label>
            <input
              type="number"
              min={1}
              max={10000}
              value={customMbps}
              onChange={(e) => setCustomMbps(Math.max(1, Number(e.target.value)))}
              className="textinput"
              style={{ marginTop: 0, fontSize: 16, fontWeight: 700 }}
            />
          </div>
        </div>

        {/* Big result callout */}
        <div style={{ marginTop: 22, padding: 18, background: "rgba(0, 255, 102, 0.06)", border: "1px solid rgba(0, 255, 102, 0.25)", borderRadius: 12, textAlign: "center" }}>
          <span style={{ fontSize: 13, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Estimated transfer duration
          </span>
          <div style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 900, color: "var(--lime)", margin: "4px 0", letterSpacing: "-0.02em" }}>
            {formatSeconds(effectiveBits / (customMbps * 1_000_000))}
          </div>
          <small className="hint">
            Calculated for {sizeValue} {sizeUnit} at {customMbps} Mbps with protocol framing
          </small>
        </div>
      </div>

      <section style={{ marginBottom: 28 }}>
        <h2>Speed comparison table for {sizeValue} {sizeUnit}</h2>
        <table>
          <thead>
            <tr>
              <th>Connection type</th>
              <th>Typical bandwidth</th>
              <th>Estimated duration</th>
            </tr>
          </thead>
          <tbody>
            {SPEED_PRESETS.map((p) => {
              const sec = effectiveBits / (p.speedMbps * 1_000_000);
              return (
                <tr key={p.label}>
                  <td style={{ fontWeight: 600 }}>{p.label}</td>
                  <td>{p.speedMbps} Mbps</td>
                  <td style={{ color: "var(--lime)", fontWeight: 700 }}>{formatSeconds(sec)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: 28 }}>
        <h2>Why is upload speed usually slower than download speed?</h2>
        <p>
          Most residential internet connections (cable and VDSL) are <b>asymmetric</b>: a 100 Mbps download connection often has an
          upload speed of only 10–20 Mbps. Fiber connections are symmetrical (e.g. 300 Mbps up and down). Check your speed test to
          verify your actual upload speed.
        </p>
      </section>

      <div style={{ padding: 22, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", textAlign: "center", marginTop: 28 }}>
        <h3 style={{ margin: "0 0 8px" }}>Ready to send your file?</h3>
        <p className="hint" style={{ margin: "0 0 16px" }}>
          LimeShare uploads in parallel chunks directly to encrypted edge storage clusters with zero bandwidth throttling.
        </p>
        <Link to="/" className="btn" style={{ textDecoration: "none" }}>
          <UploadCloud size={16} /> Start Transfer Now
        </Link>
      </div>
    </article>
  );
}
